import { z } from 'zod';
import { HexSchema } from '../hex/index.js';
import { ContentIdSchema } from './data/common.js';
import { ToolIdSchema } from './data/explore.js';
import { RelightDepthSchema } from './data/memory-puzzles.js';
import { ExploreNotableSchema } from './events.js';
import { ItemCountsSchema } from './inventory.js';
import { queryInt } from './query.js';

// Exploring your land (#199): zoom into a tile you own and search its spots
// with your Keeper and crafted tools. The server sends where the spots are
// and which are done, never what they hold (CLAUDE.md rule 6), and rolls
// every find (rule 1).

/**
 * Dev only (`HP_DEV_SQUISHY_GRANTS`): `POST /maps/:mapId/dev/homestead`, a
 * fully explored tile of mine next to home (#295). An e2e can ask for its
 * land and resource spot, or for one that hasn't joined home (#242).
 */
export const DevHomesteadRequestSchema = z.strictObject({
  /** Its land (a terrain id); otherwise what's there, or cleared land. */
  terrain: ContentIdSchema.optional(),
  /** A resource spot in its middle (a resource id); otherwise none. */
  nodeResource: ContentIdSchema.optional(),
  /**
   * False: explored, but three steps from home, so it hasn't joined.
   * 'napping': the same, but a homestead once, cut off from home since.
   */
  joined: z.union([z.boolean(), z.literal('napping')]).optional(),
});
export type DevHomesteadRequest = z.infer<typeof DevHomesteadRequestSchema>;

/** `GET /maps/:mapId/explore?q=&r=`: one of my tiles. */
export const ExploreQuerySchema = z.object({
  q: queryInt({ min: -32768, max: 32767 }),
  r: queryInt({ min: -32768, max: 32767 }),
});

/** One search spot as the explore view draws it. */
export const PublicSearchSpotSchema = z.object({
  index: z.number().int().min(0),
  kind: ContentIdSchema,
  tool: ToolIdSchema.nullable(),
  /** Tile-local position (`hexToWorld` at size 1: the tile's corner is 1 away). */
  x: z.number(),
  z: z.number(),
  done: z.boolean(),
});
export type PublicSearchSpot = z.infer<typeof PublicSearchSpotSchema>;

/** A tile's homestead state for its owner: part of home, cut off, or neither. */
export const HomesteadStateSchema = z.enum(['joined', 'paused']).nullable();

/**
 * A grey village tile's puzzle (#306 PR 2b, "Relight the path", or "The
 * forgotten story" on the heart tile): its broken thing, how deep in the Hollow the tile is (that sets the board's size),
 * where it stands and whether I've fixed it. The board itself comes with
 * each try (`POST …/explore/puzzle/start`); what fixing it gives stays
 * server-side.
 */
export const HollowedPuzzleSchema = z.object({
  /** Its look (`MEMORY_PUZZLES.skins[].id`). */
  skin: ContentIdSchema,
  /** What it holds: Relight the path, or The forgotten story on the heart tile. */
  kind: z.enum(['relight', 'story']),
  depth: RelightDepthSchema,
  /** Where it stands, tile-local (`hexToWorld` at size 1, like the spots). */
  x: z.number(),
  z: z.number(),
  /** I've fixed it already (once a tile each, `tile_explore.puzzle_solved_at`). */
  solved: z.boolean(),
  /** The story's torn pages I've found in this Hollow (one a tile); it wakes once I have them all. */
  pages: z.object({ found: z.number().int().min(0), total: z.number().int().min(1) }).optional(),
});
export type HollowedPuzzle = z.infer<typeof HollowedPuzzleSchema>;

/**
 * One cell of a Relight board: the heart, a lantern, a path piece, an echo
 * shadow or an empty slot. `open` is its openings as they are now (a 4-bit
 * mask: north 1, east 2, south 4, west 8), never how it sits when solved.
 * A `loose` (wooden) piece slides and never turns.
 */
export const RelightCellSchema = z.object({
  kind: z.enum(['heart', 'lantern', 'pipe', 'shadow', 'empty']),
  open: z.number().int().min(0).max(15),
  loose: z.boolean(),
});
export type RelightCell = z.infer<typeof RelightCellSchema>;

/** A Relight board, row by row from the top, and how many moves it allows. */
export const RelightBoardSchema = z.object({
  size: z.number().int().min(3).max(8),
  /** The heart's cell (bottom row): where the light comes from. */
  heart: z.number().int().min(0),
  limit: z.number().int().min(1),
  cells: z.array(RelightCellSchema).max(64),
});
export type RelightBoard = z.infer<typeof RelightBoardSchema>;

/** One move: a quarter turn of a piece, or a wooden piece's slide into the empty slot beside it. */
export const RelightMoveSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('turn'), at: z.number().int().min(0).max(63) }),
  z.strictObject({
    type: z.literal('slide'),
    at: z.number().int().min(0).max(63),
    to: z.number().int().min(0).max(63),
  }),
]);
export type RelightMove = z.infer<typeof RelightMoveSchema>;

/**
 * A grey village tile's explore extras (#306 2a), on `ExploreTileResponse`
 * when the tile is an unrestored Hollowed village tile. Its spots refill
 * each morning (map time), so `joy` is today's colour.
 */
export const HollowedExploreSchema = z.object({
  /** The village (`MapView.communities[].id`) and its name for the header. */
  id: z.uuid(),
  name: z.string(),
  /** How much colour today's searches brought back, 0–100 (`exploreJoy`). */
  joy: z.number().int().min(0).max(100),
  /** Where each Hollow echo drifts, tile-local (`hexToWorld` at size 1, like the spots). */
  echoes: z.array(z.object({ x: z.number(), z: z.number() })),
  /** Its Relight puzzle (PR 2b). */
  puzzle: HollowedPuzzleSchema.optional(),
});
export type HollowedExplore = z.infer<typeof HollowedExploreSchema>;

/** How far along a tile is: "7 of 12 found 🔍". */
export const ExploreProgressSchema = z.object({
  searched: z.number().int().min(0),
  total: z.number().int().min(0),
});

/** The explore view of one of my tiles. */
export const ExploreTileResponseSchema = z.object({
  q: HexSchema.shape.q,
  r: HexSchema.shape.r,
  terrain: z.string(),
  /**
   * The terrain whose explore kit the tile uses, when not its own (#621
   * W1-13): `river` on land beside a river. Absent: its own.
   */
  kit: z.string().optional(),
  spots: z.array(PublicSearchSpotSchema),
  /** Every tool the tile's spots call for, in tool order (hands need none). */
  needs: z.array(ToolIdSchema),
  progress: ExploreProgressSchema,
  homestead: HomesteadStateSchema,
  /** Uses left of each tool in my bag (a tool is counted in uses). */
  tools: z.record(ToolIdSchema, z.number().int().min(0)),
  /** A grey village tile (#306 2a); absent on my own land. */
  community: HollowedExploreSchema.optional(),
});
export type ExploreTileResponse = z.infer<typeof ExploreTileResponseSchema>;

/** `POST /maps/:mapId/explore/search`: search one spot on one of my tiles. */
export const SearchSpotRequestSchema = z.strictObject({
  q: HexSchema.shape.q,
  r: HexSchema.shape.r,
  spot: z.number().int().min(0),
});
export type SearchSpotRequest = z.infer<typeof SearchSpotRequestSchema>;

/** What a search found and what it changed, for the find card. */
export const SearchSpotResponseSchema = z.object({
  spot: z.number().int().min(0),
  /** Resources and other items found (may be empty: "Just a wiggly worm!"). */
  found: ItemCountsSchema,
  /** A lore page found, by id and title; its words come from `GET /lore`. */
  lore: z.object({ id: ContentIdSchema, title: z.string() }).nullable(),
  /** A piece of clothing found (wardrobe item id). */
  clothing: ContentIdSchema.nullable(),
  notable: ExploreNotableSchema.nullable(),
  /** XP each team squishy got. */
  xp: z.array(z.object({ squishyId: z.uuid(), xp: z.number().int().min(0) })),
  /** The tool it used and its uses left, or null for hands. */
  tool: z.object({ id: ToolIdSchema, usesLeft: z.number().int().min(0) }).nullable(),
  progress: ExploreProgressSchema,
  /** This search finished the tile. */
  explored: z.boolean(),
  homestead: HomesteadStateSchema,
  /** The bag after the search. */
  items: ItemCountsSchema,
  /** On a grey village tile (#306 2a): its joy after this search, 0–100. */
  joy: z.number().int().min(0).max(100).optional(),
  /** The forgotten story's torn page, on a grey village tile's first search (#306): my pages after it. */
  tornPage: z.object({ found: z.number().int().min(1), total: z.number().int().min(1) }).optional(),
  /**
   * The search was the cellar key's glinting spot (`KEY_SPOT`, owner decision
   * 2026-10-22): the village whose key it found, by id and name. Absent otherwise.
   */
  cellarKey: z.object({ communityId: z.uuid(), name: z.string() }).optional(),
});
export type SearchSpotResponse = z.infer<typeof SearchSpotResponseSchema>;

/** `POST /maps/:mapId/explore/puzzle/start`: a new try at a grey village tile's puzzle (#306 PR 2b). */
export const StartPuzzleRequestSchema = z.strictObject({
  q: HexSchema.shape.q,
  r: HexSchema.shape.r,
});
export type StartPuzzleRequest = z.infer<typeof StartPuzzleRequestSchema>;

/** One moment card of the forgotten story: what happened, never where it goes. */
export const StoryCardSchema = z.object({
  id: ContentIdSchema,
  emoji: z.string().min(1).max(8),
  text: z.string().min(1).max(40),
});
export type StoryCard = z.infer<typeof StoryCardSchema>;

/**
 * A forgotten story try as the kid sees it: the moments as dealt (never in
 * order) and the clue written on each torn page, page 1 first. The day's
 * order stays on the server (CLAUDE.md rule 1).
 */
export const StoryDealSchema = z.object({
  moments: z.array(StoryCardSchema).min(3).max(6),
  clues: z.array(z.string().min(1).max(160)).min(1).max(12),
});
export type StoryDeal = z.infer<typeof StoryDealSchema>;

/** A story try: its number, sent back with the order, and its pages. */
export const StoryStartSchema = z.object({
  kind: z.literal('story'),
  attempt: z.number().int().min(1),
  story: StoryDealSchema,
});
export type StoryStart = z.infer<typeof StoryStartSchema>;

/**
 * This try's puzzle: every try is a fresh puzzle of the same size (owner
 * decision: a reshuffle). A Relight board, or on the heart tile a story.
 */
export const StartPuzzleResponseSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('relight'),
    /** The try's number, sent back with its moves. */
    attempt: z.number().int().min(1),
    board: RelightBoardSchema,
  }),
  StoryStartSchema,
]);
export type StartPuzzleResponse = z.infer<typeof StartPuzzleResponseSchema>;

/** `POST /maps/:mapId/explore/puzzle`: relight a grey village tile's path (#306 PR 2b). */
export const SolvePuzzleRequestSchema = z.strictObject({
  q: HexSchema.shape.q,
  r: HexSchema.shape.r,
  /** The try these moves were made on (the latest start's). */
  attempt: z.number().int().min(1),
  /** Every move, in order; the server replays them (CLAUDE.md rule 1). */
  moves: z.array(RelightMoveSchema).max(200),
});
export type SolvePuzzleRequest = z.infer<typeof SolvePuzzleRequestSchema>;
/**
 * Dev only (`HP_DEV_SQUISHY_GRANTS`, never in production):
 * `POST /maps/:mapId/dev/relight-answer { q, r }`, my latest try's answer,
 * so an e2e or a capture can play a win through the real board.
 */
export const DevRelightAnswerResponseSchema = z.object({
  attempt: z.number().int().min(1),
  moves: z.array(RelightMoveSchema),
});
export type DevRelightAnswerResponse = z.infer<typeof DevRelightAnswerResponseSchema>;

/** `POST /maps/:mapId/explore/puzzle/story`: tell the heart tile's forgotten story (#306). */
export const TellStoryRequestSchema = z.strictObject({
  q: HexSchema.shape.q,
  r: HexSchema.shape.r,
  /** The try this order was made on (the latest start's). */
  attempt: z.number().int().min(1),
  /** The moments, first to last; the server checks it against the try's day (rule 1). */
  order: z.array(ContentIdSchema).min(3).max(6),
});
export type TellStoryRequest = z.infer<typeof TellStoryRequestSchema>;

/**
 * Dev only (`HP_DEV_SQUISHY_GRANTS`, never in production):
 * `POST /maps/:mapId/dev/story-answer { q, r }`, my latest story try's order,
 * so an e2e or a capture can tell it right through the real card.
 */
export const DevStoryAnswerResponseSchema = z.object({
  attempt: z.number().int().min(1),
  order: z.array(ContentIdSchema),
});
export type DevStoryAnswerResponse = z.infer<typeof DevStoryAnswerResponseSchema>;

/**
 * Dev only (`HP_DEV_SQUISHY_GRANTS`, never in production):
 * `POST /maps/:mapId/dev/story-pages { q, r, leave? }` finds every torn page
 * of that Hollow but `leave` of them (the heart tile's last), so an e2e or a
 * capture can wake the story without walking every tile. Replies with my pages.
 */
export const DevStoryPagesRequestSchema = z.strictObject({
  q: HexSchema.shape.q,
  r: HexSchema.shape.r,
  leave: z.number().int().min(0).max(12).optional(),
});
export type DevStoryPagesRequest = z.infer<typeof DevStoryPagesRequestSchema>;
export const DevStoryPagesResponseSchema = z.object({
  found: z.number().int().min(0),
  total: z.number().int().min(1),
});

/** What fixing it gave: the server rolls it (CLAUDE.md rule 1). */
export const SolvePuzzleResponseSchema = z.object({
  /** The puzzle, fixed now. */
  puzzle: HollowedPuzzleSchema,
  /** A lore page, by id and title; its words come from `GET /lore`. */
  lore: z.object({ id: ContentIdSchema, title: z.string() }).nullable(),
  /** A Rekindled piece found (wardrobe item id, #306 PR 5), or null. */
  clothing: ContentIdSchema.nullable(),
  /** Items: the thank-you when there's no page left to find, and any Rekindled finds (may be empty). */
  found: ItemCountsSchema,
  /** The bag after it. */
  items: ItemCountsSchema,
});
export type SolvePuzzleResponse = z.infer<typeof SolvePuzzleResponseSchema>;

/**
 * What telling the story did: whole again (what fixing it gave, as a Relight
 * win), or smudged. A smudged try is spent: it says how many moments were in
 * the right place and which, and the ink shifts to the next try's day (owner
 * decision: a failed attempt reshuffles).
 */
export const TellStoryResponseSchema = z.discriminatedUnion('result', [
  SolvePuzzleResponseSchema.extend({ result: z.literal('whole') }),
  z.object({
    result: z.literal('smudged'),
    /** How many moments were in the right place. */
    right: z.number().int().min(0),
    /** Place by place, first to last: was it right? */
    marks: z.array(z.boolean()),
    /** The next try: a new day of the same size, on the same pages. */
    next: StoryStartSchema,
  }),
]);
export type TellStoryResponse = z.infer<typeof TellStoryResponseSchema>;
